/**
 * Purchase Data Helpers
 *
 * Data layer functions for querying purchases related to member accounts.
 * These functions isolate Purchase model dependencies to prevent circular dependencies
 * between MemberAccount and Purchase models.
 */
import type { MedplumClient } from "@medplum/core";
import type { SearchOptions } from "@vibrant/models/fhir/FhirResource";
import type { ResourceRef } from "@vibrant/models/fhir/fhir-resource-types";
import { Purchase } from "./Purchase";

/**
 * Searches for all purchases associated with an account.
 * Returns Purchase instances with their service IDs.
 */
export async function searchPurchasesForAccount(
  medplum: MedplumClient,
  accountRef: ResourceRef,
): Promise<Purchase[]> {
  return Purchase.search(medplum, { account: accountRef });
}

/**
 * Searches for Hint charges (purchases) associated with an account with pagination.
 * Filters for purchases that have Hint charge item coding.
 */
export async function searchHintChargesForAccount(
  medplum: MedplumClient,
  accountRef: ResourceRef,
  options?: SearchOptions,
): Promise<{
  charges: Purchase[];
  totalCount: number;
}> {
  return Purchase.searchHintCharges(medplum, accountRef, options);
}

/**
 * Searches for Hint memberships (purchases) associated with an account.
 * Can return multiple memberships per patient.
 * Optionally supports pagination.
 */
export async function searchHintMembershipsForAccount(
  medplum: MedplumClient,
  accountRef: ResourceRef,
  options?: SearchOptions,
): Promise<{
  memberships: Purchase[];
  totalCount: number;
}> {
  return Purchase.searchHintMemberships(medplum, accountRef, options);
}
